import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CanvasService } from '../../canvas.service';
import { DomSanitizer } from '@angular/platform-browser';
import {
  ChatTypes,
  ConverstationStatus,
  SCORE_CARD_DESCRIPTION,
  SCORE_CARD_VALUES,
} from '../../canvas.model';
import { CanvasHelperService } from '../../canvas-helper.service';
import { CanvasApiHandlerService } from '../../canvas-api-handler.service';

@Component({
  selector: 'app-canvas-chat-item',
  templateUrl: './canvas-chat-item.component.html',
  styleUrl: './canvas-chat-item.component.scss',
})
export class CanvasChatItemComponent implements OnInit, OnChanges {
  @Input() chatItem: any;
  @Input() isLastConversation: boolean = false;
  @Input() isEmbededChat: boolean = false;
  @Input() responseCount: number = 0;
  @Input() userEmail: string = '';
  sourcesList: any = [];
  stats: any;
  currentResponseItem: any = '';
  convertedMessage: any = '';
  currentResponseDocument!: any;
  currentResponseDocumentVersion!: any;
  scorecardSummary!: any;
  chatTypes = ChatTypes;
  conStatus = ConverstationStatus;
  isRefreshing = false;
  @ViewChild('contentDiv', { static: false }) contentDiv!: ElementRef;
  public currentResponseIndex: number = 0;

  constructor(
    public hubService: CanvasService,
    public helperService: CanvasHelperService,
    public readonly sanitizer: DomSanitizer,
    private readonly canvasApiHandlerService: CanvasApiHandlerService
  ) {}

  ngOnInit() {
    this.getResponseItemHtml();
    this.getSources();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const responseChanged =
      changes['responseCount']?.currentValue !=
      changes['responseCount']?.previousValue;

    if (responseChanged) {
      this.currentResponseIndex =
        this.chatItem?.responses?.length > 0
          ? this.chatItem.responses.length - 1
          : 0;
    }

    const statusChanged =
      changes['chatItem']?.currentValue?.status !=
      changes['chatItem']?.previousValue?.status;

    if (responseChanged || statusChanged || this.hasDocumentChanged(changes)) {
      this.getResponseItemHtml();
      this.getSources();
    }
  }

  hasDocumentChanged(changes: SimpleChanges): boolean {
    let currentResponse = changes['chatItem']?.currentValue?.responses;
    let previousResponse = changes['chatItem']?.previousValue?.responses;

    if (!currentResponse || !previousResponse) return true;

    for (const currItem of currentResponse) {
      const prevItem = previousResponse?.find((p: any) => p.id === currItem.id);
      if (
        !prevItem ||
        JSON.stringify(currItem?.document ?? {}) !==
          JSON.stringify(prevItem?.document ?? {})
      ) {
        return true;
      }
    }

    return false;
  }

  getResponseItemHtml() {
    this.currentResponseDocument =
      this.chatItem?.responses[this.currentResponseIndex]?.document ?? null;

    this.currentResponseDocumentVersion =
      this.currentResponseDocument?.versions?.[
        this.currentResponseDocument?.versions?.length - 1
      ] ?? null;

    if (this.currentResponseDocumentVersion)
      this.currentResponseDocumentVersion.partialContent =
        this.sanitizer.bypassSecurityTrustHtml(
          this.hubService.convertDocuemntContentToHtml(
            this.currentResponseDocumentVersion?.content ?? '',
            false
          )
        );

    this.currentResponseItem =
      this.chatItem?.responses[this.currentResponseIndex];

    this.convertedMessage = this.sanitizer.bypassSecurityTrustHtml(
      this.currentResponseItem?.convertedMessage ?? ''
    );

    this.getScorecardSummary();
  }

  getScorecardSummary() {
    if (
      this.currentResponseDocument?.type == ChatTypes.SCORECARD &&
      this.currentResponseDocumentVersion?.scorecard_summary
    ) {
      let value = Number(
        this.currentResponseDocumentVersion?.scorecard_summary
          ?.awarded_points ?? 0
      );
      let total = Number(
        this.currentResponseDocumentVersion?.scorecard_summary?.total_points ??
          0
      );
      let assessment = this.checkScoreIndicator(
        this.currentResponseDocumentVersion?.scorecard_summary
          ?.overall_assesment
      );

      this.currentResponseDocumentVersion.scorecard_summary = {
        ...this.currentResponseDocumentVersion?.scorecard_summary,
        awarded_points: value,
        total_points: total,
        percentage: `${(value / total) * 100}, 100`,
        styleClass: assessment.styleClass,
        description: assessment.description,
        text: `${value}/${total}`,
      };
    }
  }

  refreshContent() {
    this.currentResponseDocument.error = false;
    this.hubService.isLoadingDocument = this.hubService.isContentEditorOpened;
    this.hubService.contentForkCalls(
      [
        {
          document_id: this.currentResponseDocument.document_id,
          id: this.chatItem._id,
        },
      ],
      this.hubService.currentChatMessages,
      this.hubService.isContentEditorOpened
    );
  }

  onOutlineClicked() {
    if (this.currentResponseDocument.document_id) {
      this.hubService.triggerClearCanvasEvent();
      this.hubService.openCanvasForDocumentChatResponse(
        {
          version: this.currentResponseDocumentVersion?.version,
          document_id: this.currentResponseDocument.document_id,
          conversation_id: this.chatItem?._id,
        },
        0
      );
    }
  }

  onTemporaryOutlineClicked() {
    this.hubService.openCanvasForDocumentChatResponse(null, 500, true);
  }

  getSources() {
    this.sourcesList =
      this.chatItem?.responses[this.currentResponseIndex]?.sources ?? [];

    this.stats =
      this.chatItem?.responses[this.currentResponseIndex]?.stats ?? null;
  }

  getIndexLengthString() {
    return `${this.currentResponseIndex + 1}/${
      this.chatItem.responses?.length
    }`;
  }

  decrementIndex() {
    if (this.currentResponseIndex != 0) {
      this.currentResponseIndex -= 1;
    } else {
      this.currentResponseIndex = this.chatItem.responses.length - 1;
    }
  }

  incrementIndex() {
    if (this.currentResponseIndex == this.chatItem.responses.length - 1) {
      this.currentResponseIndex = 0;
    } else {
      this.currentResponseIndex += 1;
    }
  }

  updateCanvas() {
    if (
      this.hubService.isContentEditorOpened &&
      this.currentResponseDocument?.versions?.length > 0 &&
      this.currentResponseDocument.document_id
      // && (!this.hubService.outlineDocument?._id ||
      //   this.hubService.outlineDocument?._id ==
      //     this.currentResponseDocument.document_id)
    ) {
      this.hubService.isLoadingDocument = true;
      this.hubService.openCanvasForDocumentChatResponse({
        document_id: this.currentResponseDocument.document_id,
        version:
          this.currentResponseDocument.versions[
            this.currentResponseDocument.versions.length - 1
          ].version,
        conversation_id: this.chatItem?._id,
      });
    }
  }

  copyResponse() {
    this.hubService.copyResponse(this.contentDiv);
  }

  onSourceClicked() {
    this.hubService.openCitationsDialog(this.sourcesList);
  }

  checkScoreIndicator(reference: string): any {
    if (reference?.includes(SCORE_CARD_VALUES.OUTSTANDING)) {
      return {
        description: SCORE_CARD_DESCRIPTION.OUTSTANDING,
        styleClass: 'very-low',
      };
    } else if (reference?.includes(SCORE_CARD_VALUES.VERY_GOOD)) {
      return {
        description: SCORE_CARD_DESCRIPTION.VERY_GOOD,
        styleClass: 'low',
      };
    } else if (reference?.includes(SCORE_CARD_VALUES.ACCEPTABLE)) {
      return {
        description: SCORE_CARD_DESCRIPTION.ACCEPTABLE,
        styleClass: 'medium',
      };
    } else if (reference?.includes(SCORE_CARD_VALUES.MARGINAL)) {
      return {
        description: SCORE_CARD_DESCRIPTION.MARGINAL,
        styleClass: 'high',
      };
    } else if (reference?.includes(SCORE_CARD_VALUES.UNACCEPTABLE)) {
      return {
        description: SCORE_CARD_DESCRIPTION.UNACCEPTABLE,
        styleClass: 'very-high',
      };
    } else {
      return {
        description: 'N/A',
        styleClass: 'na',
      };
    }
  }

  isPositiveFeedback(responseItem: any): boolean {
    return responseItem?.feedback?.feedback_type === 'positive';
  }

  isNegativeFeedback(responseItem: any): boolean {
    return responseItem?.feedback?.feedback_type === 'negative';
  }

  onRefreshClicked() {
    this.isRefreshing = true;
    this.hubService
      .checkIfAnyConversationInProgress(this.chatItem)
      .then((isInProgress) => {
        this.isRefreshing = false;
      })
      .catch(() => {
        this.isRefreshing = false;
      });
  }
}
